import type { Event } from '@sentry/types';

const REDACTED_VALUE = '[Filtered]';

const isSensitiveKey = (key: string): boolean => {
  const normalizedKey = key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  return (
    normalizedKey.includes('password') ||
    normalizedKey === 'passwd' ||
    normalizedKey === 'pwd' ||
    normalizedKey.includes('secret') ||
    normalizedKey.includes('authorization') ||
    normalizedKey.includes('cookie') ||
    normalizedKey.includes('apikey') ||
    normalizedKey === 'card' ||
    normalizedKey.includes('cardnumber') ||
    normalizedKey.includes('cardholder') ||
    normalizedKey.includes('creditcard') ||
    normalizedKey.includes('debitcard') ||
    normalizedKey === 'usercard' ||
    normalizedKey === 'pan' ||
    normalizedKey === 'cvv' ||
    normalizedKey === 'cvc' ||
    normalizedKey === 'ccv' ||
    normalizedKey.includes('securitycode') ||
    normalizedKey.includes('securitynumber') ||
    normalizedKey.includes('expiry') ||
    normalizedKey.includes('expiration') ||
    normalizedKey.includes('expmonth') ||
    normalizedKey.includes('expyear') ||
    normalizedKey.includes('iban') ||
    normalizedKey.includes('bankaccount') ||
    normalizedKey.includes('accountnumber') ||
    normalizedKey.includes('routingnumber') ||
    normalizedKey.includes('paymentintent') ||
    normalizedKey.includes('paymentmethod') ||
    normalizedKey.includes('giftcard') ||
    normalizedKey.includes('redemptioncode') ||
    normalizedKey === 'voucher' ||
    normalizedKey === 'otp' ||
    normalizedKey.includes('verificationcode') ||
    normalizedKey === 'token' ||
    normalizedKey.endsWith('token')
  );
};

const sanitizeString = (value: string, sanitizeObject: (value: unknown) => unknown): string => {
  const trimmedValue = value.trim();

  // Axios often stores request bodies as JSON strings. Parse those strings so
  // nested secrets are scrubbed using the same rules as object data.
  if (trimmedValue.startsWith('{') || trimmedValue.startsWith('[')) {
    try {
      return JSON.stringify(sanitizeObject(JSON.parse(trimmedValue)));
    } catch {
      // Some diagnostic strings contain a JSON object with a text prefix.
    }
  }

  // Also cover diagnostic strings such as "payload: {\"password\":\"...\"}".
  return value.replace(
    /((?:["']?)[a-zA-Z0-9_.-]*(?:password|passwd|pwd|secret|authorization|cookie|api[_-]?key|card|cvv|cvc|ccv|expiry|expiration|exp[_-]?(?:month|year)|iban|account[_-]?number|routing[_-]?number|payment[_-]?(?:intent|method)|gift[_-]?card|redemption[_-]?code|voucher|otp|verification[_-]?code|token)[a-zA-Z0-9_.-]*(?:["']?)[ \t]*[:=][ \t]*)(["'][^"']*["']|[^,\s}\]]+)/gi,
    `$1${REDACTED_VALUE}`
  );
};

const sanitizeSentryData = (value: unknown, seen = new WeakSet<object>()): unknown => {
  if (typeof value === 'string') {
    return sanitizeString(value, (parsedValue) => sanitizeSentryData(parsedValue));
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (seen.has(value)) {
    return '[Circular]';
  }

  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeSentryData(item, seen));
  }

  return Object.entries(value).reduce<Record<string, unknown>>((result, [key, item]) => {
    result[key] = isSensitiveKey(key) ? REDACTED_VALUE : sanitizeSentryData(item, seen);

    return result;
  }, {});
};

export const sanitizeSentryEvent = (event: Event): Event => sanitizeSentryData(event) as Event;

export const sanitizeSentryExtras = <T extends object>(extras: T): T =>
  sanitizeSentryData(extras) as T;
